import React, { FC, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { Cart, LineItem } from 'shared/types/cart';
import { ExtendedLineItem } from 'shared/types/cart/LineItem';
import { CloudinaryProductAssets, ContentfulProduct } from 'shared/types/content';
import { Variant } from 'shared/types/product';
import { ExtendedProductDetailProduct } from 'shared/types/product/Product';
import {
  CART_CUSTOM_LINE_ITEM_ID_QUERY_PARAM,
  CART_LINE_ITEM_ID_QUERY_PARAM,
} from 'helpers/constants/query-params';
import usePath from 'helpers/hooks/usePath';
import { getProductGalleryImages } from 'helpers/mappers/getProductGalleryImages';
import { toUIProduct } from 'helpers/mappers/toUIProduct';
import { toUIProductContent } from 'helpers/mappers/toUIProductContent';
import CustomProductDetails, { CustomProductDetailsProps } from '..';
import RichText from '../../../../atoms/rich-text';
import { getProductParentCategory } from '../../product-details/helpers/getProductParentCategory';
import { getImageCacheKey } from '../../product-details/helpers/imageCacheKey';
import { usePrefetchLeadAxisImages } from '../../product-details/hooks/usePrefetchLeadAxisImages';
import { useProductBaseImages } from '../../product-details/hooks/useProductBaseImages';

type ProductDetailsAdapterProps = {
  assets?: CloudinaryProductAssets;
  cart?: Cart;
  content?: ContentfulProduct;
  inModalVersion?: CustomProductDetailsProps['inModalVersion'];
  onAddToCart?: (
    variant: Variant,
    quantity: number,
    finish?: string,
    sheen?: string,
    hasWirebrush?: boolean,
    options?: { skipAnalytics?: boolean },
  ) => Promise<Cart>;
  origin?: string;
  feedLifestyleImage?: string;
  product: ExtendedProductDetailProduct;
  removeLineItem?: (item: LineItem & { customLineItemId?: string }) => Promise<void>;
  updateLineItem?: (
    lineItemId: string,
    customLineItemId: string | undefined,
    quantity: number,
  ) => Promise<{ isError: boolean; data?: Cart; error?: unknown }>;
  reviewListProps?: CustomProductDetailsProps['reviewListProps'];
  setIsOpen?: (value: boolean) => void;
  wirebrushModal?: CustomProductDetailsProps['wirebrushModal'];
  woodFinishHelper?: CustomProductDetailsProps['woodFinishHelper'];
};

const CustomProductDetailsAdapter: FC<ProductDetailsAdapterProps> = ({
  product,
  content,
  assets,
  woodFinishHelper,
  wirebrushModal,
  inModalVersion,
  cart,
  setIsOpen,
  onAddToCart,
  removeLineItem,
  updateLineItem,
  reviewListProps,
  origin,
  feedLifestyleImage,
}) => {
  const { path } = usePath();
  const searchParams = useSearchParams();
  const category = getProductParentCategory(product);

  const [variant, setVariant] = useState(() => {
    if (inModalVersion) {
      return product?.variants[0];
    } else {
      const currentVariantPath = path.split('/');

      // access position 3 since variantPath = ['', 'slug', 'p', 'sku', '']
      const currentVariantSKU = currentVariantPath[3]?.split('?')[0];

      const currentVariantIndex = product?.variants.findIndex(({ sku }) => sku == currentVariantSKU);

      return product.variants[currentVariantIndex] ?? product.variants?.[0];
    }
  });

  const mappedProduct = toUIProduct(product, variant);
  const mappedContent = toUIProductContent(content, assets?.shared);
  // Cache the image by the attributes that can actually change it, while fetching with the current
  // SKU: a difference the camera cannot see (system angle, dimensions) is a cache hit, and every
  // difference it can see refetches. Seeded by the server payload for the initial SKU.
  const imageKey = getImageCacheKey(variant);
  const baseImageGroup = useProductBaseImages(product.productId, imageKey, variant.sku, assets?.bySku?.[variant.sku]);
  // Warm the other materials/species in the background so switching the lead axis is instant.
  usePrefetchLeadAxisImages(product.productId, product.variants, variant);
  const galleryImages = getProductGalleryImages(baseImageGroup, assets?.shared, variant.sku, variant.images);
  const editLineItemId = searchParams.get(CART_LINE_ITEM_ID_QUERY_PARAM);
  const editLineItem = cart?.lineItems?.find((lineItem: ExtendedLineItem) => {
    if (!editLineItemId || lineItem.lineItemId !== editLineItemId) return false;

    const customLineItemId = searchParams.get(CART_CUSTOM_LINE_ITEM_ID_QUERY_PARAM);
    return customLineItemId ? lineItem.customLineItemId === customLineItemId : !lineItem.customLineItemId;
  });
  const isCartItemEditPending = !!editLineItemId && !editLineItem;

  const handleChangeVariant = (sku: string) => {
    const variantsToUse = product.variants.find((variant) => variant.sku === sku);

    if (variantsToUse) {
      // The tab title is product-level (the server's meta title), so a variant switch leaves it alone.
      setVariant(variantsToUse);
    }
  };

  const description = mappedContent?.blurb ? (
    <RichText content={mappedContent.blurb} variant="small" className="text-tertiary" />
  ) : undefined;

  const plainTextDescription = mappedContent?.blurb ? documentToPlainTextString(mappedContent.blurb) : undefined;

  return (
    <CustomProductDetails
      product={{
        ...mappedProduct,
        feedLifestyleImage,
        images: galleryImages,
      }}
      woodFinishHelper={woodFinishHelper}
      wirebrushModal={wirebrushModal}
      productContent={mappedContent}
      description={description}
      plainTextDescription={plainTextDescription}
      variant={variant}
      category={category}
      url={product._url}
      inModalVersion={inModalVersion}
      onChangeVariant={handleChangeVariant}
      setIsOpen={setIsOpen}
      onAddToCart={onAddToCart}
      removeLineItem={removeLineItem}
      updateLineItem={updateLineItem}
      editLineItem={editLineItem}
      isCartItemEditPending={isCartItemEditPending}
      reviewListProps={reviewListProps}
      origin={origin}
      categoryCtaText={mappedContent?.categoryCtaText}
      categoryCtaUrl={mappedContent?.categoryCtaUrl}
    />
  );
};

export default CustomProductDetailsAdapter;
